import { accordionFragment } from '@/app/components/client/accordion/fragment';
import { bulletsFragment } from '@/app/components/client/bullets/fragment';
import { flatButtonFragment } from '@/app/components/client/button/fragment';
import { cardFragment } from '@/app/components/client/card/fragment';
import { carouselFragment } from '@/app/components/client/carousel/fragment';
import { expandCollapseFragment } from '@/app/components/client/expand-collapse/fragment';
import { progressFragment } from '@/app/components/client/progress/fragment';
import { storiesFragment } from '@/app/components/client/stories/fragment';
import { storytellingFragment } from '@/app/components/client/storytelling/fragment';
import { userCardFragment } from '@/app/components/client/user-card/fragment';
import { type SecondaryNav } from '@/app/components/client/navigation-tabs';
import { secondaryNavFragment } from '@/app/components/client/navigation-tabs/fragment';
import { cmsVideoFragment } from '@/app/components/server/cms-video/fragment';
import { type DatoSeoCourse } from '@/app/components/server/cohort-selector/components/seo-course-structured-data';
import { seoCourseFragment } from '@/app/components/server/cohort-selector/components/seo-course-structured-data/fragment';
import { cohortSelectorFragment } from '@/app/components/server/cohort-selector/fragment';
import { type LegacyPdpHeader } from '@/app/components/server/course-page-header/legacy-pdp-header';
import { legacyPdpHeaderFragment } from '@/app/components/server/course-page-header/legacy-pdp-header/fragment';
import { type Hero2026 } from '@/app/components/server/hero-2026';
import { hero2026Fragment } from '@/app/components/server/hero-2026/fragment';
import { highlightedTextFragment } from '@/app/components/server/highlighted-text/fragment';
import { infoMediaFragment } from '@/app/components/server/info-media/fragment';
import { type PageBuilderModule } from '@/app/components/server/page-builder-module';
import {
  moduleForPageBuilderFragment,
  moduleForSecondaryNavigationFragment,
} from '@/app/components/server/page-builder-module/fragment';
import { roundedBannerFragment } from '@/app/components/server/rounded-banner/fragment';
import { statsFragment } from '@/app/components/server/stats/fragment';
import { tagFragment } from '@/app/components/server/tag/fragment';
import { type CohortSelector } from '@/app/l/[region]/[language]/pdp/[course]/[campus]/[[...extraParams]]/modules/cohort-selector';
import { request } from '@/lib/datocms/client';
import {
  buttonFragment,
  companyFragment,
  listWithIconsFragment,
  metaTagsFragment,
  moduleFormatFragment,
  openGraphImageField,
  responsiveImageFragment,
  storyFragment,
  targetFragment,
  videoFragment,
} from '@/lib/datocms/fragments';
import { datoCmsIdMap } from '@/lib/datocms/id-map';
import {
  type DatoBackgroundColor,
  type DatoButtonBlock,
  type DatoImage,
} from '@/lib/datocms/types';
import { urlToDatoLocale } from '@/lib/utils/locale';
import { type SeoMetadata } from '@/lib/utils/meta-tags';
import { type CoursePageParams } from './page';

type CoursePageQuery<TModule> = { coursePage: TModule };

const getPageQuery = (
  fields: string,
  fragments: string
) => `query CoursePageQuery(
        $regionId: ItemId!,
        $courseId: ItemId!,
        $locale: SiteLocale!,
      ) {
        coursePage(
          filter: {
            region: { eq: $regionId },
            course: { eq: $courseId },
          },
          locale: $locale,
          fallbackLocales: en_US,
        ) {
          ${fields}
        }
      }
      ${fragments}
  `;

export const fetchModuleData = async <TModule>(
  params: CoursePageParams,
  fields: string,
  fragments = ''
): Promise<TModule> => {
  const { region, language, course } = params;
  const locale = urlToDatoLocale[`${region}${language}`];
  const regionId = datoCmsIdMap.regions[region];
  const courseId = datoCmsIdMap.courses[course];
  const query = getPageQuery(fields, fragments);

  const coursePageQueryResult = await request<CoursePageQuery<TModule>>({
    query,
    variables: { regionId, courseId, locale },
  });

  return coursePageQueryResult.coursePage;
};

const pageQuery = `
  query CoursePageQuery(
    $courseId: ItemId!
    $locale: SiteLocale!
    $regionId: ItemId!
  ) {
    coursePage(
      filter: {
        course: { eq: $courseId }
        region: { eq: $regionId }
      }
      locale: $locale
    ) {
      _seoMetaTags { ...metaTagsFragment }
      cohortSelector {
        __typename
        ... on CohortSelectorModuleRecord { ...cohortSelectorFragment }
      }
      course { ...seoCourseFragment }
      headerModule {
        __typename
        ... on Hero2026Record {
          ...hero2026Fragment
          openGraphImage: image {
            ${openGraphImageField}
          }
        }
        ... on PdpPageHeaderRecord {
          ...legacyPdpHeaderFragment
          openGraphImage: image {
            ${openGraphImageField}
          }
        }
      }
      hideCohortPrices
      navAndHeaderBackground { backgroundColor }
      pageBuilder {
        __typename
        ... on ModuleForPageBuilderRecord { ...moduleForPageBuilderFragment }
        ... on ModuleForSecondaryNavigationRecord {
          ...moduleForSecondaryNavigationFragment
        }
      }
      pageTitle
      secondaryNav { ...secondaryNavFragment }
    }
    navbar(
      fallbackLocales: en_US
      filter: {
        region: { eq: $regionId }
      }
      locale: $locale
    ) {
      button { ...buttonFragment }
    }
  }

  ${buttonFragment}
  ${cohortSelectorFragment}
  ${hero2026Fragment}
  ${legacyPdpHeaderFragment}
  ${metaTagsFragment}
  ${moduleForPageBuilderFragment}
  ${moduleForSecondaryNavigationFragment}
  ${secondaryNavFragment}
  ${seoCourseFragment}

  ${accordionFragment}
  ${bulletsFragment}
  ${cardFragment}
  ${flatButtonFragment}
  ${carouselFragment}
  ${cmsVideoFragment}
  ${companyFragment}
  ${expandCollapseFragment}
  ${highlightedTextFragment}
  ${infoMediaFragment}
  ${listWithIconsFragment}
  ${moduleFormatFragment}
  ${progressFragment}
  ${responsiveImageFragment}
  ${roundedBannerFragment}
  ${statsFragment}
  ${storiesFragment}
  ${storyFragment}
  ${storytellingFragment}
  ${tagFragment}
  ${targetFragment}
  ${userCardFragment}
  ${videoFragment}
`;

type HeaderModule = (Hero2026 | LegacyPdpHeader) & {
  openGraphImage: DatoImage | null;
};

type PageData = {
  cohortSelector: CohortSelector | null;
  course: DatoSeoCourse;
  headerModule: HeaderModule | null;
  hideCohortPrices: boolean;
  navAndHeaderBackground: DatoBackgroundColor;
  pageBuilder: PageBuilderModule[];
  pageTitle: string;
  secondaryNav: SecondaryNav;
} & SeoMetadata;

type PageQuery = {
  coursePage: PageData | null;
  navbar: {
    button: [DatoButtonBlock];
  };
};

type PageDataOptions = Partial<{
  preview: boolean;
}>;

export const getPageData = async (
  params: CoursePageParams,
  options?: PageDataOptions
): Promise<PageQuery> => {
  const { course, language, region } = params;
  const { preview } = { preview: false, ...options };

  const courseId = datoCmsIdMap.courses[course];
  const regionId = datoCmsIdMap.regions[region];
  const locale = urlToDatoLocale[`${region}${language}`];

  const pageData = await request<PageQuery>({
    preview,
    query: pageQuery,
    variables: { courseId, locale, regionId },
  });

  return pageData;
};
